import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { routeCarrierAssembly } from "@/setup/routes/mill.routes";
import { SignOffModal } from "@/shared/components/SignOffModal";
import { ErrorModal, HelmetDexteel, useMesfRealtime } from "@dexteel/mesf-core";
import { ArrowBack } from "@mui/icons-material";
import {
	Box,
	Button,
	CircularProgress,
	Container,
	Grid2,
	IconButton,
	Paper,
	Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	CarrierForm,
	CarrierFormResponse,
	FormInstanceData,
} from "../../models/CarrierForm";
import {
	answerAll,
	getCarrierFormInstance,
	processMillBuilding,
	updateCarrierStatus,
} from "../../repositories/CarrierAssemblyRepository";
import { QuestionCard } from "./QuestionCard";
import {
	carrierBuildQuestions,
	getNextQuestionKey,
	QuestionFormKey,
} from "./models/Questions";

export const useFormCarrier = (
	partId?: number,
	carrierInstanceId?: number,
	onError?: (error: Error) => void,
	enabled?: boolean,
) => {
	return useQuery<CarrierFormResponse, Error>({
		queryKey: ["carrierForm", partId, carrierInstanceId],
		queryFn: ({ signal }) =>
			getCarrierFormInstance({ partId, carrierInstanceId }, signal),
		onError: onError,
		enabled: enabled ?? true,
	});
};

export const CarrierBuildFormPage = () => {
	const navigate = useNavigate();

	const { carrierId, carrierInstanceId } = useParams<{
		carrierId: string;
		carrierInstanceId: string;
	}>();

	const isViewRoute = location.pathname.includes("/view");
	const isBuildRoute = location.pathname.includes("/build");
	const isSignRoute = location.pathname.includes("/sign");
	const answerAllRoute = location.pathname.includes("answer-all");

	const [notificationMessage, setNotificationMessage] = useState<string>("");
	const [error, setError] = useState("");
	const [userAnswers, setUserAnswers] = useState<Record<QuestionFormKey, any>>(
		{} as Record<QuestionFormKey, any>,
	);
	const [visibleQuestionIds, setVisibleQuestionIds] = useState<Set<string>>(
		new Set(),
	);
	const [canSubmit, setCanSubmit] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [openSignDialog, setOpenSignDialog] = useState(false);
	const [openDialogRaw, setOpenDialogRaw] = useState(false);

	const { data, isLoading } = useFormCarrier(
		Number(carrierId),
		Number(carrierInstanceId),
	);
	const carrier = data?.carrierData;
	const formData = data?.formData;
	const rollsPasses = data?.passesData;

	const queryClient = useQueryClient();
	const answer = useMutation(
		() => {
			setIsSubmitLoading(true);
			return answerAll({
				partId: Number(carrierId) || carrier?.PartId || null,
			});
		},
		{
			onSuccess: async () => {
				await queryClient.invalidateQueries({
					queryKey: [
						"carrierForm",
						Number(carrierId),
						Number(carrierInstanceId),
					],
				});
			},
			onError: (error: Error) => {
				setError(error.message);
			},
			onSettled: () => setIsSubmitLoading(false),
		},
	);
	const saveForm = useMutation(
		({
			partId,
			rollsetId,
			questionKey,
			value,
		}: {
			partId: number;
			rollsetId?: number | null;
			questionKey?: QuestionFormKey | null;
			value?: string | null;
		}) => {
			setIsSubmitLoading(true);
			return processMillBuilding({
				partId,
				rollsetId,
				questionKey,
				value,
			});
		},
		{
			onSuccess: async () => {
				await queryClient.invalidateQueries({
					queryKey: [
						"carrierForm",
						Number(carrierId),
						Number(carrierInstanceId),
					],
				});
			},
			onError: (error: Error, variables) => {
				setError(error.message);

				const questionToRemove = variables.rollsetId
					? "RollsetId"
					: variables.questionKey;

				if (questionToRemove) {
					setUserAnswers((prev) => {
						const newAnswers = { ...prev };
						newAnswers[questionToRemove] = null;
						return newAnswers;
					});

					const newAnswers = { ...userAnswers, [questionToRemove]: null };
					const newlyVisibleQuestionIds = recalculateVisibleQuestions(
						newAnswers,
						carrier,
					);
					setVisibleQuestionIds(newlyVisibleQuestionIds);
				}
			},
			onSettled: () => setIsSubmitLoading(false),
		},
	);
	const submitForm = useMutation(
		() => {
			setIsSubmitLoading(true);
			return updateCarrierStatus({
				partId: Number(carrierId),
				newStatus: "C",
			});
		},
		{
			onSuccess: async () => {
				navigate(`/${routeCarrierAssembly}/${carrierId}`);
				await queryClient.invalidateQueries({
					queryKey: [
						"carrierForm",
						Number(carrierId),
						Number(carrierInstanceId),
					],
				});
				await queryClient.invalidateQueries(["carriers"]);
			},
			onError: (error: Error) => {
				setError(error.message);
			},
			onSettled: () => setIsSubmitLoading(false),
		},
	);

	const getInitialVisibleQuestions = (): Set<string> => {
		const initialQuestions = new Set<string>();

		if (carrierBuildQuestions.length === 0) {
			return initialQuestions;
		}

		const firstQuestion = carrierBuildQuestions[0];
		initialQuestions.add(firstQuestion.key);

		let currentQuestion = firstQuestion;
		while (true) {
			if (typeof currentQuestion.nextQuestion === "string") {
				const nextKey = currentQuestion.nextQuestion;
				if (nextKey) {
					initialQuestions.add(nextKey);
					const nextQuestionIndex = carrierBuildQuestions.findIndex(
						(q) => q.key === nextKey,
					);
					if (nextQuestionIndex !== -1) {
						currentQuestion = carrierBuildQuestions[nextQuestionIndex];
					} else {
						break;
					}
				} else {
					break;
				}
			} else {
				break;
			}
		}

		return initialQuestions;
	};

	const shouldShowPassQuestion = (
		questionKey: QuestionFormKey,
		carrierData?: CarrierForm,
	): boolean => {
		if (!carrierData) return false;

		if (questionKey === "49_Passes.RollsPass") {
			return (
				carrierData.TopRollType === "E" &&
				!!carrierData.TopRollPasses &&
				carrierData.BottomRollType === "E" &&
				!!carrierData.BottomRollPasses
			);
		}
		return true;
	};

	const recalculateVisibleQuestions = (
		currentAnswers: Record<string, any>,
		carrierData?: CarrierForm,
	): Set<string> => {
		const recalculatedVisibleIds = new Set<string>();

		if (carrierBuildQuestions.length === 0) {
			return recalculatedVisibleIds;
		}

		const initialQuestions = getInitialVisibleQuestions();
		initialQuestions.forEach((id) => recalculatedVisibleIds.add(id));

		for (const currentQuestion of carrierBuildQuestions) {
			if (!recalculatedVisibleIds.has(currentQuestion.key)) {
				continue;
			}

			if (
				currentQuestion.key === "49_Passes.RollsPass" &&
				!shouldShowPassQuestion(currentQuestion.key, carrierData)
			) {
				recalculatedVisibleIds.delete(currentQuestion.key);
				const nextKey = getNextQuestionKey(currentQuestion, null);
				if (nextKey) {
					recalculatedVisibleIds.add(nextKey);
				}
				continue;
			}

			const currentAnswer = currentAnswers[currentQuestion.key];
			if (currentAnswer == null) {
				continue;
			}

			const nextQuestionKey = getNextQuestionKey(
				currentQuestion,
				currentAnswer,
			);
			if (nextQuestionKey) {
				recalculatedVisibleIds.add(nextQuestionKey);
			}
		}

		return recalculatedVisibleIds;
	};

	const orderedVisibleQuestions = carrierBuildQuestions
		.filter((q) => visibleQuestionIds.has(q.key))
		.sort((a, b) => a.order - b.order);

	const isQuestionAnswered = (questionKey: QuestionFormKey): boolean => {
		return userAnswers[questionKey] != null;
	};

	const updateCanSubmitState = () => {
		const allAnswered = orderedVisibleQuestions.every((question) =>
			isQuestionAnswered(question.key),
		);
		setCanSubmit(allAnswered);
	};

	const onAnswerSelected = (questionId: string, selectedOption: any) => {
		const newAnswers = { ...userAnswers, [questionId]: selectedOption };
		setUserAnswers(newAnswers);

		const newlyVisibleQuestionIds = recalculateVisibleQuestions(
			newAnswers,
			carrier,
		);
		setVisibleQuestionIds(newlyVisibleQuestionIds);
	};

	const initializeAllValues = (
		carrierValues?: CarrierForm,
		formValues?: FormInstanceData[],
	) => {
		setUserAnswers((prevAnswers) => {
			const initialAnswers: Record<string, any> = { ...prevAnswers };

			if (formValues && formValues.length > 0) {
				for (const f of formValues) {
					if (f.QuestionKey) {
						let answerValue: any = f.IsAnswered ? f.Answer : null;
						// Convert pass answers to numbers for proper comparison
						if (
							f.QuestionKey === "49_Passes.RollsPass" &&
							answerValue != null
						) {
							answerValue = parseInt(answerValue, 10);
							if (isNaN(answerValue)) answerValue = null;
						}
						// Only update if server has a value, or if we don't have a local value
						if (answerValue != null || prevAnswers[f.QuestionKey] == null) {
							initialAnswers[f.QuestionKey] = answerValue;
						}
					}
				}
			}

			if (carrierValues) {
				// Only update from carrier values if they have data
				if (carrierValues.SectionId != null) {
					initialAnswers["4_MillBuilding.Section"] = carrierValues.SectionId;
				}
				if (carrierValues.StandId != null) {
					initialAnswers["11_MillBuilding.StandNumber"] = carrierValues.StandId;
				}
				if (carrierValues.RollsetId != null) {
					initialAnswers["RollsetId"] = carrierValues.RollsetId;
				}
				if (carrierValues.Bait != null) {
					initialAnswers["15_MillBuilding.SetBait"] = carrierValues.Bait;
				}
			}

			return initialAnswers;
		});
	};

	// Separate effect to recalculate visible questions after userAnswers changes
	useEffect(() => {
		if (carrier) {
			const newlyVisibleQuestionIds = recalculateVisibleQuestions(
				userAnswers,
				carrier,
			);
			setVisibleQuestionIds(newlyVisibleQuestionIds);
		}
	}, [userAnswers, carrier]);

	useEffect(() => {
		if (carrierBuildQuestions.length > 0) {
			setVisibleQuestionIds(getInitialVisibleQuestions());
		}

		const initialAnswers: Record<string, any> = {};
		carrierBuildQuestions.forEach((q) => {
			initialAnswers[q.key] = null;
		});
		setUserAnswers(initialAnswers);
	}, []);

	useEffect(() => {
		initializeAllValues(carrier, formData);
	}, [carrier, formData]);

	useEffect(() => {
		updateCanSubmitState();
	}, [userAnswers, visibleQuestionIds]);

	useMesfRealtime({
		onReceiveMessage: async (author, message) => {
			if (
				message.replaceAll("[", "").replaceAll("]", "") ==
				"M2.GetCarrierFormInstance"
			) {
				await queryClient.invalidateQueries({
					queryKey: [
						"carrierForm",
						Number(carrierId),
						Number(carrierInstanceId),
					],
				});
			}
		},
	});

	if (isLoading) {
		return <CenteredLazyLoading />;
	}

	if (!carrier) {
		return (
			<Box
				sx={{
					backgroundColor: "#f8f9fa",
					minHeight: "100vh",
					paddingTop: 3,
					paddingBottom: 4,
				}}
			>
				<Container maxWidth="md">
					<Paper
						sx={{
							padding: 4,
							textAlign: "center",
							backgroundColor: "white",
							borderRadius: 3,
							boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
							border: "1px solid #e0e0e0",
						}}
					>
						<Typography variant="h6" color="error" gutterBottom>
							Carrier not found
						</Typography>
						<Typography variant="body2" color="textSecondary">
							Unable to load the requested carrier information.
						</Typography>
					</Paper>
				</Container>
			</Box>
		);
	}

	return (
		<>
			<HelmetDexteel title={`${carrier.CarrierName} - Carrier Build Form`} />
			<Box
				sx={{
					backgroundColor: "#f8f9fa",
					minHeight: "100vh",
					paddingTop: 3,
					paddingBottom: 4,
				}}
			>
				<Box sx={{ margin: "10px 0 0 30px" }}>
					<IconButton
						size="medium"
						onClick={() => navigate(`/${routeCarrierAssembly}/${carrierId}`)}
						color="primary"
						title="Back to carriers"
					>
						<ArrowBack fontSize="medium" />
					</IconButton>
				</Box>
				<Container maxWidth="md">
					<Paper
						sx={{
							backgroundColor: "white",
							borderRadius: 3,
							boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
							border: "1px solid #e0e0e0",
							overflow: "hidden",
						}}
					>
						<Box
							sx={{
								background: (theme) => theme.palette.primary.main,
								color: "white",
								padding: (theme) => theme.spacing(4, 4, 3),
								textAlign: "left",
							}}
						>
							<Typography
								variant="h4"
								sx={{
									fontWeight: 400,
									fontSize: "2rem",
									marginBottom: 1,
								}}
							>
								Carrier {carrier.CarrierName}
							</Typography>
							<Typography
								variant="subtitle1"
								sx={{
									opacity: 0.9,
									fontSize: "1rem",
									fontWeight: 300,
								}}
							>
								Build Form
							</Typography>
						</Box>

						<Box sx={{ padding: (theme) => theme.spacing(3, 4) }}>
							<Typography variant="body2" color="textSecondary" paragraph>
								Please complete all questions below. Your progress will be saved
								automatically.
							</Typography>

							<Grid2 container spacing={2}>
								{orderedVisibleQuestions.map((question, index) => (
									<Grid2 size={{ xs: 12 }} key={question.key}>
										<QuestionCard
											question={question}
											index={index + 1}
											answer={userAnswers[question.key]}
											onAnswerChange={(answer) => {
												if (
													question.key === "4_MillBuilding.Section" ||
													question.key === "11_MillBuilding.StandNumber"
												) {
													const clearedAnswers: Record<QuestionFormKey, any> = {
														...userAnswers,
														[question.key]: answer,
														RollsetId: null,
														"49_Passes.RollsPass": null,
														"12_MillBuilding.TopArborNumber": null,
														"13_MillBuilding.BottomArborNumber": null,
														"14_MillBuilding.RollSetLetter": null,
													};
													setUserAnswers(clearedAnswers);
													const newlyVisibleQuestionIds =
														recalculateVisibleQuestions(
															clearedAnswers,
															carrier,
														);
													setVisibleQuestionIds(newlyVisibleQuestionIds);
												} else {
													onAnswerSelected(question.key, answer);
												}

												if (carrierId == null) return;

												if (question.key === "RollsetId") {
													saveForm.mutate({
														partId: Number(carrierId),
														rollsetId: answer,
													});
												} else if (question.key === "49_Passes.RollsPass") {
													saveForm.mutate({
														partId: Number(carrierId),
														questionKey: question.key,
														value: answer?.toString(),
													});
												} else {
													saveForm.mutate({
														partId: Number(carrierId),
														questionKey: question.key,
														value: answer,
													});
												}
											}}
											disabled={isSubmitLoading || isSignRoute || isViewRoute}
											standId={carrier.StandId}
											standName={carrier.StandName}
											sectionId={carrier.SectionId}
											sectionName={carrier.SectionName}
											topRoll={{
												id: carrier.TopRollId,
												name: carrier.TopRollName,
												type: carrier.TopRollType,
											}}
											bottomRoll={{
												id: carrier.BottomRollId,
												name: carrier.BottomRollName,
												type: carrier.BottomRollType,
											}}
											rollsPasses={rollsPasses}
										/>
									</Grid2>
								))}
							</Grid2>

							{!isViewRoute && (
								<Box
									sx={{
										marginTop: 4,
										padding: 3,
										textAlign: "center",
									}}
								>
									<Button
										variant="contained"
										color="primary"
										size="large"
										onClick={() =>
											isBuildRoute
												? setOpenDialogRaw(true)
												: setOpenSignDialog(true)
										}
										disabled={!canSubmit || isSubmitLoading}
										sx={{
											borderRadius: 2,
											padding: (theme) => theme.spacing(1.5, 4),
											fontSize: "1rem",
											fontWeight: 500,
											textTransform: "none",
										}}
										startIcon={
											isSubmitLoading ? (
												<CircularProgress size={20} color="inherit" />
											) : null
										}
										data-test-id={
											isBuildRoute ? "submit-form-button" : "sign-off-button"
										}
									>
										{isBuildRoute ? "Submit Form" : "Sign Off"}
									</Button>
								</Box>
							)}

							{answerAllRoute && (
								<Button
									variant="contained"
									sx={{
										position: "fixed",
										bottom: 3,
										right: 3,
										zIndex: 1000,
										borderRadius: "50px",
										padding: (theme) => theme.spacing(1.5, 3),
										textTransform: "none",
										boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
										transition: "all 0.2s ease",
									}}
									onClick={() => answer.mutate()}
									disabled={
										isSubmitLoading ||
										!orderedVisibleQuestions.some(
											(q) => userAnswers[q.key] == null && q.order >= 16,
										) ||
										!orderedVisibleQuestions.some((q) => q.order >= 16)
									}
									startIcon={
										isSubmitLoading ? (
											<CircularProgress size={16} color="inherit" />
										) : null
									}
									color="primary"
								>
									Answer All
								</Button>
							)}
						</Box>
					</Paper>
				</Container>
			</Box>
			<ConfirmationDialogRaw
				title={"Confirm Submit"}
				suffixTitle={carrier.CarrierName ?? ""}
				message={`Are you sure you want to submit the form?`}
				open={openDialogRaw}
				onClose={(value?: string) => {
					if (value == "OK") {
						submitForm.mutate();
					}
					setOpenDialogRaw(false);
				}}
			/>
			<SignOffModal
				open={openSignDialog}
				onHide={(success) => {
					setOpenSignDialog(false);
					if (success) navigate(`/${routeCarrierAssembly}/${carrierId}`);
				}}
				partId={Number(carrierId)}
				info={{
					partName: carrier.CarrierName,
					sectionName: carrier.SectionName,
					standName: carrier.StandName,
				}}
			/>
			<NotificationSnackBar
				message={notificationMessage}
				severity="info"
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
